import React, { useState } from "react";
import { base44 } from "../api/base44Client";
import { useQuery, useMutation } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { CheckCircle, AlertTriangle, Loader2 } from "lucide-react";

export default function AcceptInvitation() {
  const urlParams = new URLSearchParams(window.location.search);
  const token = urlParams.get('token');
  const [processing, setProcessing] = useState(false);
  const [result, setResult] = useState(null);

  const { data: invitation } = useQuery({
    queryKey: ['invitation', token],
    queryFn: async () => { const invitations = await base44.entities.AdminInvitation.list(); return invitations.find(inv => inv.token === token && inv.status === 'en_attente'); },
    enabled: !!token
  });

  const acceptMutation = useMutation({
    mutationFn: async () => {
      const user = await base44.auth.me();
      await base44.auth.updateMe({ admin_role:invitation.role, admin_added_by:invitation.invited_by, admin_added_date:new Date().toISOString() });
      await base44.entities.AdminInvitation.update(invitation.id, { status:'accepte' });
      await base44.entities.AdminLog.create({ action:'invitation_acceptee', admin_email:invitation.invited_by, target_email:user.email, details:`${user.email} a accepté l'invitation`, timestamp:new Date().toISOString() });
    },
    onSuccess: () => { setResult({ success:true, message:"Invitation acceptée avec succès ! Vous êtes maintenant administrateur." }); setProcessing(false); },
    onError: () => { setResult({ success:false, message:"Erreur lors de l'acceptation de l'invitation" }); setProcessing(false); }
  });

  if (!token) return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center">
      <div className="max-w-md w-full p-8 text-center bg-white rounded-xl shadow-xl">
        <AlertTriangle className="w-16 h-16 text-red-500 mx-auto mb-4" />
        <h2 className="text-2xl font-bold mb-2">Lien invalide</h2>
        <p className="text-gray-600 mb-6">Le lien d'invitation est invalide ou a expiré.</p>
        <Link to="/accueil"><button className="accent-gradient text-white px-6 py-3 rounded-lg w-full">Retour à l'accueil</button></Link>
      </div>
    </div>
  );

  if (result) return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center">
      <div className="max-w-md w-full p-8 text-center bg-white rounded-xl shadow-xl">
        {result.success ? <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" /> : <AlertTriangle className="w-16 h-16 text-red-500 mx-auto mb-4" />}
        <h2 className="text-2xl font-bold mb-2">{result.success ? 'Invitation acceptée !' : 'Erreur'}</h2>
        <p className="text-gray-600 mb-6">{result.message}</p>
        {result.success ? <Link to="/dashboard"><button className="accent-gradient text-white px-6 py-3 rounded-lg w-full font-bold">Accéder au Dashboard</button></Link> : <Link to="/accueil"><button className="accent-gradient text-white px-6 py-3 rounded-lg w-full">Retour à l'accueil</button></Link>}
      </div>
    </div>
  );

  if (!invitation) return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center">
      <div className="max-w-md w-full p-8 text-center bg-white rounded-xl shadow-xl">
        <Loader2 className="w-16 h-16 text-[#c41e3a] animate-spin mx-auto mb-4" />
        <p className="text-gray-600">Vérification de l'invitation...</p>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center">
      <div className="max-w-md w-full p-8 text-center bg-white rounded-xl shadow-xl">
        <CheckCircle className="w-16 h-16 text-[#c41e3a] mx-auto mb-4" />
        <h2 className="text-2xl font-bold text-black mb-2">Invitation Administrateur</h2>
        <p className="text-gray-600 mb-6">Vous avez été invité à rejoindre l'équipe d'administration de R2B Entreprises.</p>
        <div className="bg-gray-100 p-4 rounded-lg mb-6 text-left">
          <p className="text-sm text-gray-600 mb-1">Email invité :</p><p className="font-medium">{invitation.email}</p>
          <p className="text-sm text-gray-600 mt-3 mb-1">Rôle :</p><p className="font-medium capitalize">{invitation.role}</p>
        </div>
        <button onClick={() => { setProcessing(true); acceptMutation.mutate(); }} disabled={processing} className="w-full accent-gradient text-white py-3 rounded-lg hover:opacity-90 font-bold flex items-center justify-center gap-2">
          {processing ? <><Loader2 className="w-4 h-4 animate-spin" />Acceptation...</> : "Accepter l'invitation"}
        </button>
      </div>
    </div>
  );
}
